import { cn } from '@repo/ui';
import { type ReactElement, useState } from 'react';

interface DesignThumbnailProps {
  /** 썸네일 서명 주소 — null 이면 아직 그림이 없다. */
  readonly url: string | null;
  readonly alt: string;
  /** 크기·모서리·바탕 덮어쓰기. 기본은 부모 너비를 채우는 정사각형이다. 목록 행은 `size-11 shrink-0 rounded-lg bg-muted`(상품 목록 44px). */
  readonly className?: string;
}

/**
 * 디자인 썸네일 칸(견적 디자인 미리보기 D11) — 둥근 연회색 정사각형에 그림을 담는다.
 * 주소가 없거나 그림을 불러오지 못하면(서명 주소 만료 등) 깨진 그림 대신 "미리보기 준비 중".
 * 실패는 주소별로 기억한다 — 다시 조회해 새 주소가 오면 다시 그린다.
 * 칸이 6rem 보다 좁으면(목록 행) 문구는 화면낭독기에만 남기고 회색 칸만 보인다. 문구는 12rem 이상 큰 칸(상세 미리보기)에서 text-sm.
 */
export const DesignThumbnail = ({ url, alt, className }: DesignThumbnailProps): ReactElement => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  return (
    <div
      className={cn(
        '@container relative aspect-square w-full overflow-hidden rounded-md bg-muted/40',
        className,
      )}
    >
      {url !== null && failedUrl !== url ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="size-full object-contain"
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <p className="flex size-full items-center justify-center p-2 text-center text-xs text-balance break-keep text-muted-foreground @max-[6rem]:sr-only @min-[12rem]:text-sm">
          미리보기 준비 중
        </p>
      )}
    </div>
  );
};
